/**
 * Orchestration des radars sur l'écran carte.
 *
 * Sépare ce qui relève du modèle — téléchargement, cache, fusion, porté par
 * `RadarManager` — de ce qui relève de l'écran : l'avertissement légal, la
 * couche carte, et la matérialisation des radars d'un parcours en marqueurs.
 *
 * Référence : `RadarController.kt`, `docs/radars.md`.
 */

import type { GpxTrack } from '../core/track'
import type { PreferencesStore } from '../core/preferences'
import type { RadarManager } from '../core/radars'
import type { Translator } from '../i18n'
import { createCustomMarker, MARKER_TYPE_RADAR } from '../core/types'
import { findRadarsNearTrack } from '../core/radarCheck'
import { showDialog } from './dialogs'
import type { Shell } from './shell'

export interface RadarControllerDeps {
  readonly shell: Shell
  readonly translator: Translator
  readonly prefs: PreferencesStore
  readonly radars: RadarManager
  /** Pose la couche des radars sur la carte. */
  readonly onDisplay: (visible: boolean) => void
  /** Redessine la trace et enregistre les modifications. */
  readonly onTrackChanged: (track: GpxTrack) => void
}

export class RadarController {
  constructor(private readonly deps: RadarControllerDeps) {}

  /**
   * Charge la base et l'affiche.
   *
   * Appelée au démarrage et après chaque changement de réglage. L'affichage
   * est rafraîchi même quand la base est vide : c'est ce qui purge la couche
   * après le retrait d'un pays.
   */
  async load(): Promise<void> {
    const prefs = this.deps.prefs
    await this.deps.radars.loadLocal(prefs.get('radarCountries'))

    this.deps.onDisplay(prefs.get('radarsEnabled'))

    // Téléchargement différé et silencieux : l'utilisateur n'a rien demandé,
    // et un échec ne doit pas l'interrompre. Les radars déjà en cache restent
    // affichés en attendant.
    if (
      prefs.get('radarsEnabled') &&
      this.deps.radars.needsRefresh(
        prefs.get('radarCountries'),
        prefs.get('radarsLastRefresh'),
      )
    ) {
      const result = await this.deps.radars.refresh(prefs.get('radarCountries'))

      // La date n'est retenue qu'en cas de succès complet : un échec partiel
      // pris pour un succès ne serait retenté qu'une semaine plus tard.
      if (result.failed.length === 0) {
        prefs.set('radarsLastRefresh', Date.now())
      }

      this.deps.onDisplay(prefs.get('radarsEnabled'))
    }
  }

  /**
   * Avertissement légal, affiché à **chaque** activation.
   *
   * Volontairement non mémorisé : la légalité des avertisseurs de radars varie
   * selon les pays traversés, et la réponse d'hier ne vaut pas pour la
   * frontière franchie ce matin.
   *
   * @returns `true` si l'utilisateur accepte.
   */
  confirmEnable(): Promise<boolean> {
    const t = this.deps.translator

    return new Promise((resolve) => {
      let accepted = false

      showDialog(document.body, {
        title: t.t('radars_warning_title'),
        body: t.t('radars_warning_message'),
        dismissible: true,
        actions: [
          { label: t.t('cancel') },
          {
            label: t.t('radars_warning_accept'),
            primary: true,
            onClick: () => {
              accepted = true
              resolve(true)
            },
          },
        ],
        // Fermer par le fond ou la touche d'échappement vaut refus : ne rien
        // décider ne peut pas valoir acceptation.
        onClose: () => {
          if (!accepted) resolve(false)
        },
      })
    })
  }

  /**
   * Matérialise en marqueurs les radars proches d'une trace.
   *
   * Idempotent : les marqueurs de type radar sont retirés avant reconstruction,
   * les autres types restent intacts. La base est lue même si l'affichage
   * global des radars est désactivé — vérifier un parcours et afficher toute
   * la base sont deux intentions distinctes.
   */
  async checkTrack(track: GpxTrack): Promise<void> {
    const t = this.deps.translator

    await this.deps.radars.loadLocal(this.deps.prefs.get('radarCountries'))

    if (!this.deps.radars.hasLocalData()) {
      this.deps.shell.showSnackbar(t.t('radar_check_need_db'))
      return
    }

    const hits = findRadarsNearTrack(track, this.deps.radars.radars)

    track.customMarkers = track.customMarkers.filter(
      (marker) => marker.type !== MARKER_TYPE_RADAR,
    )

    for (const hit of hits) {
      track.customMarkers.push(
        createCustomMarker({
          type: MARKER_TYPE_RADAR,
          name: t.t('marker_type_radar'),
          symbol: this.deps.prefs.symbolForType(MARKER_TYPE_RADAR),
          distanceKm: track.rawDistanceToMarkerKm(hit.distance),
          // Une vitesse inconnue vaut zéro dans OpenStreetMap : l'afficher
          // annoncerait une limite à 0 km/h.
          description:
            hit.radar.speedLimit > 0 ? `${hit.radar.speedLimit} km/h` : '',
        }),
      )
    }

    this.deps.onTrackChanged(track)

    this.deps.shell.showSnackbar(
      hits.length === 0
        ? t.t('radar_check_none')
        : t.t(
            hits.length === 1 ? 'radar_check_found_one' : 'radar_check_found',
            { count: hits.length },
          ),
    )
  }
}
